import { SOCIAL_OG_IMAGE, SOCIAL_OG_IMAGE_ALT } from "@/lib/social-image";
import { BlogHero } from "@/components/BlogHero";
import { blogHeroUrl, blogHeroAlt } from "@/data/_media/blog-hero";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ctrDesc, ctrTitle } from "@/lib/seo-copy";
import { SITE } from "@/lib/site-config";
import {
  buildJsonLd,
  jsonLdScript,
  webPageSchema,
  breadcrumbListSchema,
  articleSchema,
  faqPageSchema,
} from "@/lib/schema";
import { canonicalAndHreflang } from "@/lib/hreflang";

/** Long-form Thai guide: police clearance certificate for use abroad. */
const TITLE = "หนังสือรับรองความประพฤติ (Police Clearance) ไปใช้ต่างประเทศ — คู่มือฉบับเต็ม";
const DESC =
  "ขั้นตอนขอหนังสือรับรองความประพฤติจากกองบัญชาการตำรวจสันติบาล เอกสารที่ต้องเตรียม การรับรองต่อยอดที่กรมการกงสุลและสถานทูต พร้อมสาเหตุที่ถูกตีกลับ";
const URL = "/blog/police-clearance-certificate-guide";
const PUBLISHED = "2026-08-10";

const FAQ = [
  {
    question: "หนังสือรับรองความประพฤติออกโดยหน่วยงานใด",
    answer:
      "ในประเทศไทย หนังสือรับรองความประพฤติ (Police Clearance Certificate) ออกโดยกองบัญชาการตำรวจสันติบาล สำนักงานตำรวจแห่งชาติ ผู้ยื่นต้องพิมพ์ลายนิ้วมือตามแบบที่กำหนดและยื่นเอกสารแสดงตนพร้อมหลักฐานวัตถุประสงค์การใช้ เช่น หนังสือจากนายจ้างหรือหลักฐานการยื่นวีซ่า",
  },
  {
    question: "ต้องเตรียมเอกสารอะไร",
    answer:
      "โดยทั่วไปต้องมีหนังสือเดินทางที่ยังไม่หมดอายุ บัตรประจำตัวประชาชนหรือหลักฐานแทนสำหรับชาวต่างชาติ ทะเบียนบ้าน รูปถ่ายตามขนาดที่กำหนด แบบพิมพ์ลายนิ้วมือ และหลักฐานวัตถุประสงค์การใช้เอกสาร รายการจริงให้ยึดตามประกาศของหน่วยงานในวันที่ยื่น เพราะรายละเอียดอาจเปลี่ยนแปลงได้",
  },
  {
    question: "ใช้เวลานานเท่าใด",
    answer:
      "ระยะเวลาขึ้นกับปริมาณคำขอและผลการตรวจสอบประวัติของแต่ละราย จึงไม่ควรยึดตัวเลขจากแหล่งไม่เป็นทางการ แนวปฏิบัติที่ปลอดภัยคือสอบถามกำหนดจากหน่วยงานที่รับคำขอในวันที่ยื่น และเผื่อเวลาสำหรับขั้นตอนรับรองต่อยอดอีกชั้นหนึ่ง",
  },
  {
    question: "ต้องแปลและรับรองเพิ่มหรือไม่",
    answer:
      "ขึ้นกับหน่วยงานปลายทาง หลายประเทศกำหนดให้มีคำแปลภาษาอังกฤษหรือภาษาราชการของประเทศนั้น และต้องผ่านการรับรองนิติกรณ์ที่กรมการกงสุล ก่อนนำไปรับรองที่สถานทูตหรือสถานกงสุลของประเทศปลายทางอีกชั้นหนึ่ง",
  },
  {
    question: "อยู่ต่างประเทศขอได้หรือไม่",
    answer:
      "โดยหลักสามารถดำเนินการผ่านช่องทางที่หน่วยงานกำหนด เช่น การมอบอำนาจให้ผู้แทนในไทยดำเนินการ หรือส่งชุดพิมพ์ลายนิ้วมือที่จัดทำในต่างประเทศตามเงื่อนไข ควรตรวจเงื่อนไขล่าสุดก่อนจัดทำหนังสือมอบอำนาจ เพื่อให้ถ้อยคำครอบคลุมทุกขั้นตอนที่ผู้แทนต้องทำ",
  },
  {
    question: "เอกสารมีอายุการใช้งานเท่าใด",
    answer:
      "ตัวเอกสารไม่ได้ระบุวันหมดอายุในตัวเอง แต่หน่วยงานปลายทางมักกำหนดว่าต้องออกไม่เกินระยะเวลาหนึ่งก่อนวันยื่น เช่น 3 หรือ 6 เดือน จึงควรวางลำดับให้ขอเอกสารใกล้กับกำหนดยื่นจริง ไม่ควรขอไว้ล่วงหน้านานเกินไป",
  },
  {
    question: "ถูกตีกลับเพราะอะไรมากที่สุด",
    answer:
      "สาเหตุที่พบบ่อยคือการสะกดชื่อไม่ตรงกับหนังสือเดินทาง ใช้สำเนาแทนต้นฉบับ คำแปลไม่ครบทุกข้อความรวมถึงตราประทับ ลำดับการรับรองสลับขั้น และเอกสารออกก่อนวันยื่นนานกว่าที่ปลายทางกำหนด",
  },
];

export const Route = createFileRoute("/blog/police-clearance-certificate-guide")({
  component: PoliceClearanceGuide,
  loader: () => ({
    h: {
      meta: [
        { title: ctrTitle(`${TITLE} — ${SITE.name}`) },
        { name: "description", content: ctrDesc(DESC) },
        { property: "og:title", content: ctrTitle(TITLE) },
        { property: "og:description", content: ctrDesc(DESC) },
        { property: "og:type", content: "article" },
        { property: "og:url", content: `https://${SITE.canonicalHost}${URL}` },
        { name: "twitter:card", content: "summary_large_image" },
        { property: "og:image", content: blogHeroUrl("police-clearance-certificate-guide") ?? SOCIAL_OG_IMAGE },
        { property: "og:image:alt", content: blogHeroAlt("police-clearance-certificate-guide", "th") ?? SOCIAL_OG_IMAGE_ALT },
        { name: "twitter:image", content: blogHeroUrl("police-clearance-certificate-guide") ?? SOCIAL_OG_IMAGE },
        {
          name: "keywords",
          content:
            "หนังสือรับรองความประพฤติ, police clearance certificate ไทย, ขอ pcc ตำรวจสันติบาล, แปลหนังสือรับรองความประพฤติ, รับรองกรมการกงสุล pcc",
        },
      ],
      links: canonicalAndHreflang(URL),
      scripts: [
        jsonLdScript(
          buildJsonLd([
            webPageSchema({ url: URL, title: TITLE, description: DESC, lastModified: PUBLISHED, speakable: true }),
            breadcrumbListSchema([
              { name: "หน้าแรก", url: "/" },
              { name: "บทความ", url: "/blog" },
              { name: TITLE, url: URL },
            ]),
            articleSchema({
            image: blogHeroUrl("police-clearance-certificate-guide"),
              url: URL,
              headline: TITLE,
              description: DESC,
              datePublished: PUBLISHED,
              dateModified: PUBLISHED,
              articleSection: "Police Clearance",
            }),
            faqPageSchema(FAQ),
          ]),
        ),
      ],
    },
  }),
  head: ({ loaderData }) => loaderData?.h ?? {},
});

const STEPS: Array<{ title: string; detail: string }> = [
  {
    title: "1) ยืนยันข้อกำหนดของหน่วยงานปลายทางก่อน",
    detail:
      "ตรวจว่าปลายทางต้องการเอกสารฉบับภาษาใด ต้องผ่านนิติกรณ์หรือ Apostille หรือไม่ และกำหนดอายุเอกสารกี่เดือน จุดนี้เป็นตัวกำหนดลำดับทั้งหมด ถ้าข้ามไปจะเสียเวลาซ้ำ",
  },
  {
    title: "2) เตรียมชุดเอกสารแสดงตนให้สอดคล้องกัน",
    detail:
      "การสะกดชื่อบนหนังสือเดินทาง บัตรประชาชน และทะเบียนบ้าน ต้องตรงกัน หากต่างกันควรเตรียมหลักฐานการเปลี่ยนชื่อหรือคำนำหน้าไปด้วย",
  },
  {
    title: "3) ยื่นคำขอและพิมพ์ลายนิ้วมือตามแบบที่กำหนด",
    detail:
      "ต้องใช้แบบพิมพ์ลายนิ้วมือที่หน่วยงานยอมรับ หากพิมพ์จากต่างประเทศต้องตรวจว่าแบบและผู้รับรองตรงตามเงื่อนไข ไม่เช่นนั้นคำขอจะไม่ถูกรับ",
  },
  {
    title: "4) รับเอกสารและตรวจความถูกต้องทันที",
    detail:
      "ตรวจการสะกดชื่อ เลขหนังสือเดินทาง วันเดือนปีเกิด และวันที่ออกเอกสาร ก่อนนำไปสู่ขั้นรับรองต่อยอด เพราะการแก้ภายหลังเท่ากับเริ่มใหม่",
  },
  {
    title: "5) จัดทำคำแปลตามรูปแบบที่ปลายทางรับ",
    detail:
      "แปลข้อความให้ครบทุกส่วนรวมถึงข้อความบนตราประทับและลายมือชื่อ ระบุปีปฏิทินให้ชัดเจน และคงการสะกดชื่อตามหนังสือเดินทาง",
  },
  {
    title: "6) รับรองนิติกรณ์และรับรองสถานทูตตามลำดับ",
    detail:
      "ยื่นนิติกรณ์ที่กรมการกงสุล แล้วจึงนำไปรับรองที่สถานทูตหรือสถานกงสุลของประเทศปลายทางถ้าจำเป็น การสลับลำดับเป็นสาเหตุการตีกลับที่พบบ่อย",
  },
];

const MISTAKES: string[] = [
  "ขอเอกสารล่วงหน้าหลายเดือนจนเลยกรอบอายุที่ปลายทางกำหนด",
  "ใช้สำเนาไปยื่นรับรอง ทั้งที่หน่วยงานกำหนดให้ใช้ต้นฉบับ",
  "คำแปลไม่ถอดข้อความบนตราประทับ ทำให้ถือว่าคำแปลไม่ครบ",
  "สะกดชื่อในคำแปลไม่ตรงกับหนังสือเดินทางหรือเอกสารชุดอื่นในเคสเดียวกัน",
  "หนังสือมอบอำนาจเขียนแคบเกินไป ผู้แทนทำได้เพียงบางขั้นตอน",
  "ไม่เผื่อชุดสำรอง ทั้งที่หน่วยงานปลายทางไม่คืนชุดที่ยื่น",
];

function PoliceClearanceGuide() {
  return (
    <article className="mx-auto max-w-3xl px-4 py-12 prose prose-slate dark:prose-invert">
      <nav className="text-sm mb-6 not-prose">
        <Link to="/" className="text-muted-foreground hover:text-foreground">
          หน้าแรก
        </Link>
        <span className="mx-2 text-muted-foreground">/</span>
        <Link to="/blog" className="text-muted-foreground hover:text-foreground">
          บทความ
        </Link>
      </nav>

      <h1>{TITLE}</h1>
      <BlogHero slug="police-clearance-certificate-guide" locale="th" />
      <p className="answer-summary speakable-summary">
        หนังสือรับรองความประพฤติเป็นเอกสารที่ออกโดยกองบัญชาการตำรวจสันติบาล และมักถูกใช้ประกอบการยื่นวีซ่า
        สมัครงาน หรือขอใบอนุญาตในต่างประเทศ ความยากไม่ได้อยู่ที่การขอเอกสาร แต่อยู่ที่ลำดับการรับรองต่อยอด
        และกรอบอายุเอกสารที่หน่วยงานปลายทางกำหนด บทความนี้เรียบเรียงลำดับทั้งหมดตามที่ใช้งานจริง
      </p>

      <h2>1. เอกสารนี้ตอบคำถามอะไรให้หน่วยงานปลายทาง</h2>
      <p>
        หน่วยงานปลายทางไม่ได้ต้องการ “กระดาษ” แต่ต้องการคำยืนยันจากหน่วยงานรัฐไทยว่าผู้ยื่นมีหรือไม่มี
        ประวัติที่ปรากฏในฐานข้อมูล และคำยืนยันนั้นต้องตรวจสอบย้อนกลับได้ว่าออกโดยหน่วยงานจริง
        ด้วยเหตุนี้เอกสารจึงต้องเดินผ่านชั้นการรับรองที่ปลายทางคุ้นเคย ไม่ใช่แค่มีตราประทับของตำรวจ
        เมื่อเข้าใจหลักข้อนี้ ลำดับขั้นตอนทั้งหมดจะกลายเป็นเรื่องที่คาดการณ์ได้
      </p>

      <h2>2. ลำดับขั้นตอน 6 ขั้นที่ใช้ได้กับเกือบทุกเคส</h2>
      <ol>
        {STEPS.map((s) => (
          <li key={s.title}>
            <strong>{s.title}</strong> {s.detail}
          </li>
        ))}
      </ol>

      <h2>3. กรอบอายุเอกสาร — ตัวแปรที่คนพลาดมากที่สุด</h2>
      <p>
        เอกสารฉบับนี้ไม่ระบุวันหมดอายุในตัวเอง แต่หน่วยงานปลายทางกำหนดกรอบเวลาว่าต้องออกไม่เกินกี่เดือน
        ก่อนวันยื่น ผลคือถ้าขอไว้ล่วงหน้านานเกินไป เอกสารจะใช้ไม่ได้แม้ยังอยู่ในสภาพสมบูรณ์
        แนวปฏิบัติที่เราใช้คือกำหนดวันยื่นปลายทางก่อน แล้วนับย้อนกลับให้ครอบคลุมทั้งเวลาออกเอกสาร
        เวลาจัดทำคำแปล และเวลารับรองอีกสองชั้น จากนั้นจึงเริ่มขอเอกสาร
      </p>

      <h2>4. ข้อผิดพลาดที่ทำให้ต้องเริ่มใหม่</h2>
      <ul>
        {MISTAKES.map((m) => (
          <li key={m}>{m}</li>
        ))}
      </ul>

      <h2>5. กรณีอยู่ต่างประเทศหรือเดินทางกลับไทยไม่ได้</h2>
      <p>
        กรณีนี้ต้องแยกสองเรื่องออกจากกัน เรื่องแรกคือการพิมพ์ลายนิ้วมือ ซึ่งต้องทำตามแบบและผู้รับรอง
        ที่หน่วยงานยอมรับ เรื่องที่สองคือการดำเนินการยื่นและรับเอกสารในไทย ซึ่งมักทำผ่านผู้รับมอบอำนาจ
        หนังสือมอบอำนาจควรเขียนให้ครอบคลุมทั้งการยื่นคำขอ การรับเอกสาร การยื่นนิติกรณ์
        และการยื่นต่อสถานทูตปลายทาง เพราะหากเขียนแคบเกินไป ผู้แทนจะติดขัดกลางทางและต้องจัดทำหนังสือใหม่
        ซึ่งใช้เวลามากกว่าการเขียนให้ครอบคลุมตั้งแต่ต้น
      </p>

      <h2>6. คำถามที่พบบ่อย</h2>
      <dl>
        {FAQ.map((f) => (
          <div key={f.question}>
            <dt>
              <strong>{f.question}</strong>
            </dt>
            <dd>{f.answer}</dd>
          </div>
        ))}
      </dl>

      <h2>7. แหล่งอ้างอิงและบริการที่เกี่ยวข้อง</h2>
      <p className="text-sm">
        แหล่งอ้างอิง:{" "}
        <a href="https://pcscenter.sb.police.go.th/" target="_blank" rel="noreferrer">
          กองบัญชาการตำรวจสันติบาล
        </a>{" "}
        ·{" "}
        <a href="https://consular.mfa.go.th/th/publicservice/legalization" target="_blank" rel="noreferrer">
          กรมการกงสุล กระทรวงการต่างประเทศ
        </a>
      </p>
      <ul className="not-prose mt-4 grid gap-2 text-sm">
        <li>
          <Link to="/pcc" className="text-primary underline">
            บริการหนังสือรับรองความประพฤติ
          </Link>
        </li>
        <li>
          <Link to="/blog/apostille-vs-legalization-by-country" className="text-primary underline">
            Apostille กับ Legalization ต่างกันอย่างไร — เทียบรายประเทศ
          </Link>
        </li>
        <li>
          <Link to="/blog/spouse-visa-document-checklist" className="text-primary underline">
            เอกสารสำหรับวีซ่าคู่สมรส — เช็กลิสต์และลำดับการรับรอง
          </Link>
        </li>
      </ul>
      <p className="text-sm text-muted-foreground">
        ตรวจทานเนื้อหา ณ วันที่ {PUBLISHED} เนื้อหานี้เป็นข้อมูลทั่วไปเพื่อวางลำดับงาน
        ไม่ใช่คำวินิจฉัยแทนหน่วยงานเจ้าของเรื่อง กรุณายืนยันเงื่อนไขล่าสุดกับหน่วยงานปลายทางก่อนดำเนินการ
      </p>
    </article>
  );
}
